import React, { PureComponent, ChangeEvent } from 'react';
import { Trans } from 'react-i18next';
import { Button } from '@jg/react-tracking';
import Loading from '@jg/loading';
import Svg from '@jg/svg';
import * as icons from '@jg/icons';
import { IFundraisingSettingsForm } from './FundraisingSettingsForm.d';
import AmountInput from '../AmountInput/AmountInput';
import FormError from '../FormError/FormError';

class FundraisingSettingsForm extends PureComponent<IFundraisingSettingsForm> {
  onAmountInputChange = (
    event: Pick<ChangeEvent<HTMLInputElement>, 'target'>,
  ) => {
    const { setFieldValue } = this.props;
    setFieldValue('fundraisingTarget', event.target.value);
  };

  onShortNameKeyup = (event: Pick<ChangeEvent<any>, 'persist' | 'target'>) => {
    // TODO Why is this called handleOnKeyUp, its input specific
    const {
      handleOnKeyUp,
      errors,
      clearShortNameValidity,
      isFrpShortNameValid,
    } = this.props;
    if (!errors.shortName || errors.shortName.length === 0) {
      handleOnKeyUp(event);
    } else if (isFrpShortNameValid === true || isFrpShortNameValid === false) {
      clearShortNameValidity();
    }
  };

  onShortNameChange = (
    event: Pick<ChangeEvent<HTMLInputElement>, 'target'>,
  ) => {
    const { setFieldValue } = this.props;
    setFieldValue('shortName', event.target.value);
  };

  onGdprOptIn = () => {
    const { setFieldValue } = this.props;
    setFieldValue('gdprOptIn', true);
  };

  onGdprOptOut = () => {
    const { setFieldValue } = this.props;
    setFieldValue('gdprOptIn', false);
  };

  render() {
    const {
      handleSubmit,
      values,
      errors,
      touched,
      isFrpShortNameValid,
      currencyCode,
      handleBlur,
      isInFlight,
      charity,
      charityGDPRConsent,
      shortNameCheckIsInFlight,
      shortNameCheckIsError,
    } = this.props;
    const displayShortNameErrorMessage = Boolean(
      (values.shortName.length > 2 || touched.shortName) && errors.shortName,
    );
    const displayFundraisingTargetErrorMessage = Boolean(
      touched.fundraisingTarget && errors.fundraisingTarget,
    );
    const inputClass = displayShortNameErrorMessage
      ? 'jg-form-control jg-form-control--error'
      : 'jg-form-control';
    return (
      <form
        onSubmit={handleSubmit}
        autoComplete="off"
        name="fundraising-settings-form"
      >
        <AmountInput
          id="fundraisingTarget"
          name="fundraisingTarget"
          intlId="content.fundraisingsettings.target"
          value={values.fundraisingTarget}
          defaultCurrency={currencyCode} // TODO to change to currency save in the state once API is ready
          onChange={this.onAmountInputChange}
          onBlur={handleBlur}
          hasValidationError={displayFundraisingTargetErrorMessage}
        />
        {displayFundraisingTargetErrorMessage && (
          <span
            id="errors-fundraisingTarget"
            className="jg-form-error-copy jg-display-b"
            role="alert"
          >
            {errors.fundraisingTarget}
          </span>
        )}
        <p className="jg-text--medium-light jg-space-mtxs jg-space-mbsm">
          <Trans i18nKey="content.fundraisingsettings.changetarget" />
        </p>
        <label
          className="jg-h3 jg-space-mtml jg-space-mbxs jg-display-b"
          htmlFor="shortName"
        >
          <Trans i18nKey="content.fundraisingsettings.shortname" />
        </label>
        <p className="jg-text--large jg-space-mbxs">
          <Trans i18nKey="content.fundraisingsettings.shortname.subtext" />
        </p>
        <input
          id="shortName"
          className={inputClass}
          type="text"
          name="shortName"
          value={values.shortName}
          onChange={this.onShortNameChange}
          onKeyUp={this.onShortNameKeyup}
          aria-invalid={displayShortNameErrorMessage || !isFrpShortNameValid}
          aria-describedby="eg-name"
        />

        <span
          id="eg-name"
          className=" jg-text--xsmall-light jg-space-mtxs jg-space-mbml"
        >
          <Trans i18nKey="content.fundraisingsettings.shortname.egfundraisingname" />
        </span>

        {displayShortNameErrorMessage && (
          <FormError message={errors.shortName} />
        )}
        {shortNameCheckIsError && (
          <FormError
            message={<Trans i18nKey="global.shortname.errorMessage" />}
          />
        )}
        {!errors.shortName && (
          <>
            {isFrpShortNameValid &&
              !shortNameCheckIsInFlight && (
                <span
                  className="jg-form-input-success jg-display-flex jg-space-ptxs"
                  role="alert"
                >
                  <Svg
                    markup={icons.selectIcon}
                    className="jg-icon jg-fill--mountain-meadow"
                  />
                  <div className="success-color jg-space-mlxs">
                    <Trans i18nKey="content.fundraisingsettings.shortname.success.available" />
                  </div>
                </span>
              )}
            {shortNameCheckIsInFlight && (
              <div className="jg-space-ptxs jg-color--empress">
                <Trans i18nKey="global.shortname.checkingShortnameAvailability" />
              </div>
            )}
            {isFrpShortNameValid === false &&
              !shortNameCheckIsInFlight && (
                <span
                  className="jg-form-error-copy jg-display-flex"
                  role="alert"
                >
                  <div className="error-color">
                    <Trans i18nKey="content.fundraisingsettings.shortname.error.taken" />
                  </div>
                </span>
              )}
          </>
        )}
        {charityGDPRConsent && (
          <>
            <h2 className="jg-space-mtml jg-space-mbsm">
              <Trans i18nKey="content.gdpr.subheading" />
            </h2>
            <p
              className="jg-text--large jg-space-mbml"
              data-qa="gdpr-consent-text"
            >
              {charityGDPRConsent.text.replace(/{charityName}/g, charity.name)}
            </p>
            <input
              type="radio"
              id="gdprOptIn"
              name="gdprOptIn"
              className="jg-radio"
              value="true"
              onChange={this.onGdprOptIn}
              defaultChecked={values.gdprOptIn}
            />
            <label className="jg-text--medium-regular" htmlFor="gdprOptIn">
              <Trans i18nKey="content.gdpr.yes" />
            </label>
            <input
              type="radio"
              id="gdprOptOut"
              name="gdprOptIn"
              className="jg-radio"
              value="false"
              onChange={this.onGdprOptOut}
              defaultChecked={values.gdprOptIn === false}
            />
            <label className="jg-text--medium-regular" htmlFor="gdprOptOut">
              <Trans i18nKey="content.gdpr.no" />
            </label>
            <p className="jg-text--medium-regular-light  jg-space-mtsm jg-space-mbmd">
              <Trans
                i18nKey="content.gdpr.note"
                values={{ charityName: charity.name }}
              />
            </p>
          </>
        )}
        <div className="jg-tc-button-container">
          <Button
            disabled={
              isInFlight ||
              (charityGDPRConsent && values.gdprOptIn === undefined) ||
              !isFrpShortNameValid
            }
            type="submit"
            id="jg-tc-create-frp-continue-button"
            className="jg-btn jg-space-mbxl jg-space-mtml"
            trackingData={{
              action_name: 'click',
              subtype: 'button',
              event: 'click',
              event_value: 'continue',
              page_section:
                'join team > fundraising page creation > fundraising settings',
            }}
          >
            {isInFlight ? (
              <Loading size="small" colour="purple" />
            ) : (
              <Trans i18nKey="global.button.continue" />
            )}
          </Button>
        </div>
      </form>
    );
  }
}

export default FundraisingSettingsForm;
